import { DateTime } from "luxon";
import type { EmissionRecordView } from "../../../../../../apiClient/generated";

interface GroupedRecord {
    date: string;
    records: EmissionRecordView[];
    max: number;
}

export interface SiteTotal {
    siteName: string;
    date: string;
    meanDate: Date | null;
    total: number;
    records: EmissionRecordView[];
}

export const groupBridgerEmissions = (
    data: EmissionRecordView[],
    showIndividualEmissionsFromGroups: boolean,
): [EmissionRecordView[], Record<string, SiteTotal>] => {
    // Group bridger emissions, but leave other points as-is
    const toGroup: EmissionRecordView[] = [];

    // Separate points into group/non-group
    data.forEach((i) => {
        if (i.site && i.dataPoint.data?.bridger_emission_location_id) {
            toGroup.push(i);
        }
    });

    // Group by site, equipment, and day to get maximum values
    const groupedBySiteAndName: Record<string, GroupedRecord> = toGroup.reduce(
        (acc, record) => {
            const date = record.dataPoint.detectionTimestamp
                .toISOString()
                .split("T")[0];
            const key = `${
                record.site.siteName
            }|||${date}|||${record.dataPoint.data!.bridger_emission_location_id}`;

            // If key doesn't exist, initialize it.
            if (!acc[key]) {
                acc[key] = {
                    date,
                    records: [record],
                    max: record.dataPoint.detectedRate,
                };
            } else {
                // Else, add record to list and update max value if required.
                acc[key].records.push(record);
                if (acc[key].max < record.dataPoint.detectedRate) {
                    acc[key].max = record.dataPoint.detectedRate;
                }
            }

            return acc;
        },
        {},
    );

    // Compute daily site totals
    const groupedSiteTotals: Record<string, SiteTotal> = Object.entries(
        groupedBySiteAndName,
    ).reduce((acc, [key, value]) => {
        const [siteName, date] = key.split("|||");
        const siteKey = `${siteName}|||${date}`;

        if (!acc[siteKey]) {
            acc[siteKey] = {
                siteName,
                date,
                meanDate: null,
                total: 0,
                records: [],
            };
        }

        acc[siteKey].total += value.max;
        acc[siteKey].records.push(...value.records);

        // Sum all timestamps in milliseconds
        // to maintain the mean date of all records
        const records = acc[siteKey].records;
        const totalMillis = records.reduce(
            (sum: number, record: EmissionRecordView) => {
                const timestamp = record.dataPoint.detectionTimestamp.getTime();
                return sum + timestamp;
            },
            0,
        );

        // Calculate mean timestamp
        const meanMillis = totalMillis / records.length;

        acc[siteKey].meanDate = DateTime.fromMillis(meanMillis).toJSDate();

        return acc;
    }, {});

    // Exclude groups with only one record — no need to aggregate a single point
    const filteredSiteTotals = Object.fromEntries(
        Object.entries(groupedSiteTotals).filter(
            ([, value]) => value.records.length > 1,
        ),
    );

    // If flag is enabled, display all points
    let pointsToDisplay: EmissionRecordView[] = [];
    if (showIndividualEmissionsFromGroups) {
        pointsToDisplay = data;
    } else {
        // Else, filter out items already showing up in groups.
        const idsInGroups = new Set(
            Object.values(groupedSiteTotals)
                .filter((v) => v.records.length > 1)
                .flatMap((v) => v.records.map((r) => r.id)),
        );
        pointsToDisplay = data.filter((e) => !idsInGroups.has(e.id));
    }

    return [pointsToDisplay, filteredSiteTotals];
};
